import { PropsWithChildren, useMemo, useState } from 'react'
import { FacilityVtkResult, isTemperatureSimulation, useFacilityVtkResults } from 'hooks/useFacilityVtkResults'
import { camelToTitleCase } from 'utils/string'
import { useAppSelector } from '~/store/hooks'
import { formatZoneThermalComfortInputs, getComfortZoneEdges } from '../util/comfortZone'
import Label from '../styled/Label'
import { Loader } from './Loader'
import { PerformanceMetricsContainer } from 'components/PerformanceMetrics/subcomponents/PerformanceMetricsContainer'
import { useCFDResultChecksumFragment } from '~/hooks/useCFDResultChecksum'
import { useCFDUploadsContext } from '~/hooks/useCFDUploadsContext'
import { InvalidMetricsMessage } from './InvalidMetricsMessage'
import { Checkbox } from '~/ui/Checkbox'
import { PerformanceMetricsContext } from '../hooks/usePerformanceMetricsProps'
import { Select, SelectItem } from '~/ui/Select'
import { Alert } from '~/ui/Alert'
import { CFDUploads } from '~/hooks/contexts/CFDUploadsContext/util/types'

const DEFAULT_INDEX = 0
const DEFAULT_ZONE: { label: string; value: string } = { label: 'Facility', value: 'facility' }

const PerformanceMetricsControls = ({ children }: PropsWithChildren) => {
  const { checksumResults } = useCFDResultChecksumFragment()
  const { uploads } = useCFDUploadsContext()
  const { data: facilityVtkResults, error, loading } = useFacilityVtkResults()
  const comfortZones = useAppSelector(state => state.objects.present.comfortZones)

  const [selectedZoneId, setSelectedZoneId] = useState(DEFAULT_ZONE.value)
  const [selectedGoalIndex, setSelectedGoalIndex] = useState(DEFAULT_INDEX)
  const [occupantAirflow, setOccupantAirflow] = useState(true)

  const zoneOptions = useMemo(() => {
    return [DEFAULT_ZONE].concat(
      Object.values(comfortZones).map(({ id, name }, i) => ({ label: name || `Zone ${i + 1}`, value: id }))
    )
  }, [comfortZones])

  const vtkFileOptions = facilityVtkResults?.vtkResults.map(({ type }, i) => ({
    label: camelToTitleCase(typeof type === 'string' ? type : ''),
    value: i,
  }))

  const selectedComfortZone = comfortZones[selectedZoneId]
  const selectedType = facilityVtkResults?.vtkResults[selectedGoalIndex]?.type
  const isInvalidSimulation = checkIsInvalidSimulation(selectedType, checksumResults, uploads)
  const isGlobalThermalInputsOnly = isTemperatureSimulation(
    facilityVtkResults?.vtkResults[selectedGoalIndex]?.vtkUrlGroups
  )

  return (
    <PerformanceMetricsContainer>
      {isInvalidSimulation && <InvalidMetricsMessage />}
      <div className="flex gap-3 mb-1">
        <Select
          className="flex-grow"
          name="area-of-concern"
          label="Area of Concern"
          selectedKey={selectedZoneId}
          onSelectionChange={key => setSelectedZoneId(key as string)}
        >
          {zoneOptions.map(({ label, value }) => (
            <SelectItem id={value} key={value}>
              {label}
            </SelectItem>
          ))}
        </Select>
        <Select
          className="flex-grow"
          placeholder={error ? 'Error' : undefined}
          isDisabled={!!error || loading || !vtkFileOptions?.length}
          name="goal"
          label="Goal"
          selectedKey={selectedGoalIndex}
          onSelectionChange={key => setSelectedGoalIndex(key as number)}
        >
          {(vtkFileOptions ?? []).map(({ label, value }, index) => (
            <SelectItem id={value} key={index}>
              {label}
            </SelectItem>
          ))}
        </Select>
      </div>
      <Checkbox isSelected={occupantAirflow} onChange={selection => setOccupantAirflow(selection)}>
        Include Occupant-Generated Airspeed
      </Checkbox>
      {loading ? (
        <Loader label="Gathering simulation data..." style={{ minHeight: '400px' }} />
      ) : error ? (
        <Alert severity="error" className="my-2">
          Failed to get metrics data
        </Alert>
      ) : !facilityVtkResults ? (
        <Label>Please run CFD analysis in order to view performance metrics.</Label>
      ) : (
        <PerformanceMetricsContext.Provider
          value={{
            facility:
              selectedComfortZone && !isGlobalThermalInputsOnly
                ? formatZoneThermalComfortInputs(selectedComfortZone, facilityVtkResults.facility.primaryType)
                : facilityVtkResults.facility,
            areaOfConcernEdges: selectedComfortZone ? getComfortZoneEdges(selectedComfortZone.positions) : null,
            selectedVtkFiles: facilityVtkResults.vtkResults[selectedGoalIndex],
            occupantAirflow,
          }}
        >
          {children}
        </PerformanceMetricsContext.Provider>
      )}
    </PerformanceMetricsContainer>
  )
}

const checkIsInvalidSimulation = (
  selectedType: FacilityVtkResult['type'] | undefined,
  checksumResults: ReturnType<typeof useCFDResultChecksumFragment>['checksumResults'],
  uploads: CFDUploads['uploads']
) => {
  if (!uploads || !selectedType) return false
  if (selectedType === 'heatingComposite') {
    const radiantChecksum = uploads['radiantHeat']?.checksum
    const simRadiantChecksum = checksumResults?.find(r => !!r && r.internalType === 'radiantHeat')?.resultChecksum
    const unitHeatChecksum = uploads['unitHeating']?.checksum
    const simUnitHeatChecksum = checksumResults?.find(r => !!r && r.internalType === 'unitHeating')?.resultChecksum
    return (
      Boolean(radiantChecksum && simRadiantChecksum && radiantChecksum !== simRadiantChecksum) &&
      Boolean(unitHeatChecksum && simUnitHeatChecksum && unitHeatChecksum !== simUnitHeatChecksum)
    )
  } else {
    const modelChecksum = uploads[selectedType]?.checksum
    const resultChecksum = checksumResults?.find(r => !!r && r.internalType === selectedType)?.resultChecksum
    return Boolean(modelChecksum && modelChecksum !== resultChecksum)
  }
}

export { PerformanceMetricsControls }
